import React from 'react';
import { Calendar, Clock, Award, ToggleLeft, ToggleRight } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  card,
  cardTitle,
  surfaceMuted,
  microLabel,
  dividerInner,
  bodySecondary,
  motionFadeUp,
} from '../../../lib/design-system';
import { cn } from '../../../lib/utils';

const inputBase =
  "w-full rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm transition-colors focus:ring-2 focus:ring-accent1/50 focus:border-accent1";

const inputField = cn(inputBase, "px-3 py-2.5");
const textareaField = cn(inputBase, "px-3 py-2.5 resize-none");

const labelClass = "block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5";
const requiredDot = "text-red-500 ml-0.5";

const AssignmentDetailsStep = ({
  assignmentTitle, setAssignmentTitle,
  description, setDescription,
  instructions, setInstructions,
  selectedModule, setSelectedModule,
  totalPoints, setTotalPoints,
  dueDate, setDueDate,
  dueTime, setDueTime,
  courseData,
  isUngraded, setIsUngraded,
  completeIn, setCompleteIn,
  selectedBranches = ['ALL'], setSelectedBranches
}) => {
  const today = new Date().toISOString().split('T')[0];

  return (
    <motion.div {...motionFadeUp}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ── Column 1: Core Info ──────────────────────────────── */}
        <div className="lg:col-span-2 space-y-5">

          {/* Title + Description */}
          <div className={cn(card, "p-5")}>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>
                  Title <span className={requiredDot}>*</span>
                </label>
                <input
                  type="text"
                  value={assignmentTitle}
                  onChange={(e) => setAssignmentTitle(e.target.value)}
                  className={cn(
                    inputField,
                    "text-base font-medium",
                    !assignmentTitle && "border-red-300 dark:border-red-700"
                  )}
                  placeholder="e.g. Unit 3 — Probability Worksheet"
                />
              </div>
              <div>
                <label className={labelClass}>
                  Description <span className={requiredDot}>*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={cn(
                    textareaField,
                    (!description || !description.trim()) && "border-red-300 dark:border-red-700"
                  )}
                  rows="3"
                  placeholder="Brief overview of the assignment"
                />
              </div>
              <div>
                <label className={labelClass}>
                  Module <span className={requiredDot}>*</span>
                </label>
                <select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  className={cn(
                    inputField,
                    !selectedModule && "border-red-300 dark:border-red-700"
                  )}
                >
                  <option value="">Select module</option>
                  {courseData?.syllabus?.modules?.map(module => (
                    <option key={module._id} value={module._id}>
                      Module {module.moduleNumber}: {module.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Instructions</label>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  className={textareaField}
                  rows="3"
                  placeholder="Special requirements, allowed resources, submission format..."
                />
              </div>
            </div>
          </div>

          {/* Target Branches */}
          {setSelectedBranches && (() => {
            const availableBranches = [...new Set(
              (courseData?.students || []).map(s => s.branch).filter(Boolean)
            )].sort();

            if (availableBranches.length === 0) return null;

            const isAllSelected = selectedBranches.includes('ALL');

            return (
              <div className={cn(card, "p-5")}>
                <label className={labelClass}>
                  Target Branches <span className={requiredDot}>*</span>
                </label>
                <p className={cn(bodySecondary, "mb-3 -mt-0.5")}>
                  Which branches can see this assignment
                </p>
                <div className="flex flex-wrap gap-2">
                  {/* All toggle */}
                  <button
                    type="button"
                    onClick={() => {
                      if (isAllSelected) {
                        setSelectedBranches([]);
                      } else {
                        setSelectedBranches(['ALL']);
                      }
                    }}
                    className={cn(
                      "px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors",
                      isAllSelected
                        ? "bg-accent1 text-white border-accent1"
                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-accent1 hover:text-accent1"
                    )}
                  >
                    All Branches
                  </button>
                  {availableBranches.map(branch => {
                    const isSelected = isAllSelected || selectedBranches.includes(branch);
                    return (
                      <button
                        key={branch}
                        type="button"
                        disabled={isAllSelected}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedBranches(selectedBranches.filter(b => b !== branch));
                          } else {
                            setSelectedBranches([...selectedBranches, branch]);
                          }
                        }}
                        className={cn(
                          "px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors",
                          isAllSelected
                            ? "opacity-40 cursor-not-allowed bg-gray-100 dark:bg-gray-800 text-gray-400 border-gray-200 dark:border-gray-700"
                            : isSelected
                            ? "bg-accent1/10 text-accent1 border-accent1/30"
                            : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-accent1 hover:text-accent1"
                        )}
                      >
                        {branch}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        {/* ── Column 2: Settings ───────────────────────────────── */}
        <div className="space-y-5">

          {/* Deadline */}
          <div className={cn(card, "p-5")}>
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-4 w-4 text-accent1" />
              <h3 className={cardTitle}>Deadline</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className={labelClass}>
                  Due Date <span className={requiredDot}>*</span>
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={today}
                  className={cn(
                    inputField,
                    !dueDate && "border-red-300 dark:border-red-700"
                  )}
                />
              </div>
              <div>
                <label className={labelClass}>Due Time</label>
                <input
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  className={inputField}
                />
              </div>
            </div>
          </div>

          {/* Time Limit */}
          <div className={cn(card, "p-5")}>
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-accent1" />
              <h3 className={cardTitle}>Time Limit</h3>
            </div>
            <div>
              <label className={labelClass}>Duration (minutes)</label>
              <input
                type="number"
                value={completeIn || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? null : parseInt(e.target.value);
                  setCompleteIn(value);
                }}
                min="1"
                step="1"
                placeholder="No limit"
                className={inputField}
              />
              <p className={cn(bodySecondary, "mt-1.5")}>
                Leave empty for unlimited time
              </p>
            </div>
          </div>

          {/* Scoring */}
          <div className={cn(card, "p-5")}>
            <div className="flex items-center gap-2 mb-4">
              <Award className="h-4 w-4 text-accent1" />
              <h3 className={cardTitle}>Scoring</h3>
            </div>
            <div className="space-y-3">
              {/* Ungraded toggle */}
              <button
                type="button"
                onClick={() => {
                  const newValue = !isUngraded;
                  setIsUngraded(newValue);
                  if (newValue) setTotalPoints(0);
                }}
                className={cn(
                  "w-full flex items-center justify-between p-3 rounded-sm border transition-colors",
                  isUngraded
                    ? "bg-accent1/5 border-accent1/30"
                    : "bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                )}
              >
                <div className="text-left">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Ungraded</span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    No score or points
                  </p>
                </div>
                {isUngraded ? (
                  <ToggleRight className="h-6 w-6 text-accent1 flex-shrink-0" />
                ) : (
                  <ToggleLeft className="h-6 w-6 text-gray-400 flex-shrink-0" />
                )}
              </button>

              {/* Total Points */}
              {!isUngraded && (
                <div>
                  <label className={labelClass}>Total Points</label>
                  <input
                    type="number"
                    value={totalPoints}
                    onChange={(e) => setTotalPoints(Number(e.target.value))}
                    min="0"
                    step="1"
                    className={inputField}
                    placeholder="100"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AssignmentDetailsStep;
